'use client';

import { useEffect, useMemo, useState } from 'react';
import { PreviewOpen, Search as SearchIcon } from '@icon-park/react';
import { Input } from '@okee-uikit/react';
import { useMediaQuery } from '@react-hookz/web';
import cs from 'classnames';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useTranslations } from 'next-intl';
import BriefcaseIcon from 'public/images/Icons/briefcase.svg';
import PaginationLeftIcon from 'public/images/pagination-left.svg';
import PaginationRightIcon from 'public/images/pagination-right.svg';

import { McpInstallWarningBanner } from './mcp-install-warning-banner';
import {
  type SkillDetailItem,
  type SkillItem,
  SKILLS_GRID_PAGE_SIZE,
  SKILLS_LIST_PAGE_SIZE,
  SKILLS_MOBILE_BATCH_SIZE,
} from './mock-data';
import { SkillDetailOverlay } from './skill-detail-overlay';

import s from './index.module.scss';

import { Image } from '#/components/custom-image-loader';

const DEFAULT_VIEW = 'grid';
const MOBILE_BREAK_POINT = 768;
const VALID_VIEWS = new Set(['grid', 'list']);

function normalizeView(view: string | null) {
  return view && VALID_VIEWS.has(view) ? view : DEFAULT_VIEW;
}

function normalizePage(page: string | null, totalPages: number) {
  const parsedPage = Number(page);
  if (!Number.isInteger(parsedPage) || parsedPage < 1) {
    return 1;
  }
  return Math.min(parsedPage, Math.max(totalPages, 1));
}

type PaginationItem =
  | {
      type: 'ellipsis';
      key: string;
    }
  | {
      type: 'page';
      value: number;
    };

function getVisiblePages(currentPage: number, totalPages: number): PaginationItem[] {
  if (totalPages <= 1) {
    return [{ type: 'page', value: 1 }];
  }

  const pages = new Set([1, totalPages, currentPage - 1, currentPage, currentPage + 1]);
  const sortedPages = Array.from(pages)
    .filter((page) => page >= 1 && page <= totalPages)
    .sort((a, b) => a - b);

  const items: PaginationItem[] = [];

  sortedPages.forEach((page, index) => {
    if (index === 0) {
      items.push({ type: 'page', value: page });
      return;
    }

    const previousPage = sortedPages[index - 1];
    const gap = page - previousPage;

    if (gap === 2) {
      items.push({ type: 'page', value: previousPage + 1 });
    } else if (gap > 2) {
      items.push({ type: 'ellipsis', key: `ellipsis-${previousPage}-${page}` });
    }

    items.push({ type: 'page', value: page });
  });

  return items;
}

function getRouterHref(pathname: string, searchParams: URLSearchParams) {
  const nextParams = searchParams.toString();
  return nextParams ? `${pathname}?${nextParams}` : pathname;
}

function replaceSearchParams(
  pathname: string,
  router: ReturnType<typeof useRouter>,
  nextParams: URLSearchParams
) {
  router.replace(getRouterHref(pathname, nextParams), { scroll: false });
}

function updateQueryParam({
  pathname,
  router,
  searchParams,
  key,
  value,
  resetPage = false,
  resetMobilePage = false,
}: {
  pathname: string;
  router: ReturnType<typeof useRouter>;
  searchParams: URLSearchParams;
  key: string;
  value: string | null;
  resetPage?: boolean;
  resetMobilePage?: boolean;
}) {
  const nextParams = new URLSearchParams(searchParams.toString());

  if (value) {
    nextParams.set(key, value);
  } else {
    nextParams.delete(key);
  }

  if (resetPage) {
    nextParams.delete('page');
  }

  if (resetMobilePage) {
    nextParams.delete('mPage');
  }

  replaceSearchParams(pathname, router, nextParams);
}

function ViewToggle({
  view,
  onChange,
}: {
  view: string;
  onChange: (view: 'grid' | 'list') => void;
}) {
  const t = useTranslations();

  return (
    <div aria-label={t('skills_view_label')} className={s.viewToggle} role='tablist'>
      {[
        ['grid', t('skills_view_grid')],
        ['list', t('skills_view_list')],
      ].map(([value, label]) => (
        <button
          aria-selected={view === value}
          className={cs(s.viewToggleButton, { [s.active]: view === value })}
          key={value}
          role='tab'
          type='button'
          onClick={() => onChange(value as 'grid' | 'list')}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

function TooltipText({ text }: { text: string }) {
  return text ? (
    <span className={s.tooltipContent} role='tooltip'>
      {text}
    </span>
  ) : null;
}

function formatCategoriesForList(categories: string[]) {
  if (categories.length === 0) {
    return '—';
  }
  if (categories.length <= 2) {
    return categories.join(' • ');
  }
  return `${categories.slice(0, 2).join(' • ')} ...`;
}

function getSourceTypeLabel(sourceType: number) {
  return sourceType === 2 ? 'Third-Party' : 'Built by TikTok';
}

function GridCard({ skill, onOpen }: { skill: SkillItem; onOpen: (skillId: string) => void }) {
  const visibleCategories = skill.categories.slice(0, 2);
  const overflowCount = Math.max(skill.categories.length - visibleCategories.length, 0);
  const allCategoriesText = skill.categories.join(', ');

  return (
    <button className={s.gridCardButton} type='button' onClick={() => onOpen(skill.id)}>
      <article className={s.gridCard}>
        <div className={s.gridCardBody}>
          <div className={s.gridCardHeader}>
            <div className={s.skillHeading}>
              <div className={s.blockTooltipTrigger}>
                <h2>{skill.name}</h2>
                <TooltipText text={skill.name} />
              </div>
              {skill.description || skill.summary ? (
                <div className={s.gridSummary}>{skill.description || skill.summary}</div>
              ) : (
                <div className={s.gridSummary}>—</div>
              )}
            </div>
            <span
              className={s.sourceTypeBadge}
              data-source-type={skill.sourceType === 2 ? '3p' : '1p'}
            >
              {getSourceTypeLabel(skill.sourceType)}
            </span>
          </div>

          <div className={s.categoryLine}>
            {visibleCategories.length > 0 ? (
              <span className={s.categoryLineTooltip}>
                <span className={s.categoryLineText}>
                  {visibleCategories.join(' • ')}
                  {overflowCount > 0 ? ` +${overflowCount}` : ''}
                </span>
                <TooltipText text={allCategoriesText} />
              </span>
            ) : (
              <span className={s.categoryLineText}>—</span>
            )}
          </div>

          <div className={s.cardMetaRow}>
            <div className={s.cardMetaIconWrap}>
              <Image
                alt=''
                className={s.cardMetaIcon}
                height={24}
                src={BriefcaseIcon}
                aria-hidden
              />
            </div>
            <div className={s.cardMetaText}>
              <div className={s.blockTooltipTrigger}>
                <p>
                  <span className={s.cardMetaLabel}>Company:</span> {skill.company || '—'}
                </p>
                <TooltipText text={`Company: ${skill.company || '—'}`} />
              </div>
              <div className={s.blockTooltipTrigger}>
                <p>
                  <span className={s.cardMetaLabel}>Contact email:</span>{' '}
                  {skill.developerContactEmail || '—'}
                </p>
                <TooltipText text={`Contact email: ${skill.developerContactEmail || '—'}`} />
              </div>
            </div>
          </div>
        </div>
      </article>
    </button>
  );
}

function ListRow({ skill, onOpen }: { skill: SkillItem; onOpen: (skillId: string) => void }) {
  const t = useTranslations();

  return (
    <div className={s.listRow}>
      <button
        className={cs(s.listCell, s.nameCell, s.nameButton)}
        type='button'
        onClick={() => onOpen(skill.id)}
      >
        <div className={s.blockTooltipTrigger}>
          <strong>{skill.name}</strong>
          <TooltipText text={skill.name} />
        </div>
        <p>{skill.summary || '—'}</p>
      </button>
      <div className={cs(s.listCell, s.developerCell)}>
        <strong>{skill.developerContactEmail || '—'}</strong>
        <div className={s.blockTooltipTrigger}>
          <p>{skill.company || '—'}</p>
          <TooltipText text={skill.company || '—'} />
        </div>
      </div>
      <div className={s.listCell}>
        <span className={s.listTooltipTrigger}>
          <span className={s.listCategoryText}>{formatCategoriesForList(skill.categories)}</span>
          <TooltipText text={skill.categories.join(' • ')} />
        </span>
      </div>
      <div className={cs(s.listCell, s.actionCell)}>
        <button className={s.placeholderAction} type='button' onClick={() => onOpen(skill.id)}>
          <PreviewOpen size={12} theme='outline' />
          <span>{t('skills_view_action')}</span>
        </button>
      </div>
    </div>
  );
}

function Pagination({
  currentPage,
  pathname,
  router,
  searchParams,
  totalItems,
  totalPages,
  view,
}: {
  currentPage: number;
  pathname: string;
  router: ReturnType<typeof useRouter>;
  searchParams: URLSearchParams;
  totalItems: number;
  totalPages: number;
  view: string;
}) {
  const t = useTranslations();
  const visiblePages = getVisiblePages(currentPage, totalPages);
  const displayPageSize = view === 'grid' ? SKILLS_GRID_PAGE_SIZE : SKILLS_LIST_PAGE_SIZE;
  const start = totalItems === 0 ? 0 : (currentPage - 1) * displayPageSize + 1;
  const end = totalItems === 0 ? 0 : Math.min(currentPage * displayPageSize, totalItems);

  const goToPage = (page: number) => {
    const nextParams = new URLSearchParams(searchParams.toString());
    if (page <= 1) {
      nextParams.delete('page');
    } else {
      nextParams.set('page', String(page));
    }
    replaceSearchParams(pathname, router, nextParams);
  };

  return (
    <div className={s.pagination}>
      <span className={s.paginationSummary}>
        {t('skills_pagination_summary', { start, end, total: totalItems })}
      </span>
      <div className={s.paginationControls}>
        <button
          aria-label={t('skills_pagination_prev')}
          className={cs(s.paginationArrowButton, { [s.disabled]: currentPage <= 1 })}
          disabled={currentPage <= 1}
          type='button'
          onClick={() => goToPage(currentPage - 1)}
        >
          <Image
            alt=''
            className={s.paginationArrowIcon}
            height={16}
            src={PaginationLeftIcon}
            aria-hidden
          />
        </button>
        {visiblePages.map((item) =>
          item.type === 'ellipsis' ? (
            <span className={s.paginationEllipsis} key={item.key}>
              ...
            </span>
          ) : (
            <button
              aria-current={item.value === currentPage ? 'page' : undefined}
              className={cs(s.paginationNumberButton, {
                [s.active]: item.value === currentPage,
              })}
              key={item.value}
              type='button'
              onClick={() => goToPage(item.value)}
            >
              {item.value}
            </button>
          )
        )}
        <button
          aria-label={t('skills_pagination_next')}
          className={cs(s.paginationArrowButton, { [s.disabled]: currentPage >= totalPages })}
          disabled={currentPage >= totalPages}
          type='button'
          onClick={() => goToPage(currentPage + 1)}
        >
          <Image
            alt=''
            className={s.paginationArrowIcon}
            height={16}
            src={PaginationRightIcon}
            aria-hidden
          />
        </button>
      </div>
    </div>
  );
}

export function SkillsPage({
  debugResponse,
  debugLogId,
  detailDebugResponse,
  detailDebugLogId,
  initialSelectedSkillId,
  isLoggedIn,
  page,
  selectedSkillDetail,
  skills,
  subtitle,
  title,
  total,
}: {
  debugResponse: unknown;
  debugLogId: string;
  detailDebugResponse: unknown;
  detailDebugLogId: string;
  initialSelectedSkillId?: string;
  isLoggedIn: boolean;
  skills: SkillItem[];
  page: number;
  pageSize: number;
  selectedSkillDetail: SkillDetailItem | null;
  total: number;
  title: string;
  subtitle: string;
}) {
  const pathname = usePathname();
  const router = useRouter();
  const searchParams = useSearchParams();
  const t = useTranslations();
  const currentParams = new URLSearchParams(searchParams?.toString());
  const [isBannerVisible, setIsBannerVisible] = useState(true);

  useEffect(() => {
    console.log('[agentic-hub] browser response', debugResponse);
    console.log('[agentic-hub] browser x-tt-logid', debugLogId);
  }, [debugLogId, debugResponse]);

  useEffect(() => {
    if (!detailDebugResponse && !detailDebugLogId) {
      return;
    }

    console.log('[agentic-hub] browser detail response', detailDebugResponse);
    console.log('[agentic-hub] browser detail x-tt-logid', detailDebugLogId);
  }, [detailDebugLogId, detailDebugResponse]);

  const isMobileDevice = useMediaQuery(`(max-width: ${MOBILE_BREAK_POINT}px)`, {
    initializeWithValue: false,
  });
  const currentView = normalizeView(searchParams?.get('view'));
  const query = searchParams?.get('q')?.trim() || '';
  const selectedSkillId = searchParams?.get('skillId') || initialSelectedSkillId || null;
  const currentMobileStep = Math.max(1, Number(searchParams?.get('mPage')) || 1);
  const totalPages = Math.max(
    1,
    Math.ceil(total / (currentView === 'grid' ? SKILLS_GRID_PAGE_SIZE : SKILLS_LIST_PAGE_SIZE))
  );
  const currentPage = normalizePage(searchParams?.get('page'), totalPages) || page;
  const shouldUseMobileCards = Boolean(isMobileDevice);
  const mobileVisibleCount = currentMobileStep * SKILLS_MOBILE_BATCH_SIZE;
  const displayedSkills = shouldUseMobileCards ? skills.slice(0, mobileVisibleCount) : skills;
  const canLoadMoreMobile = shouldUseMobileCards && displayedSkills.length < total;
  const fallbackSkill = useMemo(
    () => skills.find((skill) => skill.id === selectedSkillId) || null,
    [skills, selectedSkillId]
  );

  const openSkillDetail = (skillId: string) => {
    const nextParams = new URLSearchParams(currentParams.toString());
    nextParams.set('skillId', skillId);
    replaceSearchParams(pathname, router, nextParams);
  };

  const closeSkillDetail = () => {
    const nextParams = new URLSearchParams(currentParams.toString());
    nextParams.delete('skillId');
    replaceSearchParams(pathname, router, nextParams);
  };

  const handleMobileLoadMore = () => {
    const nextParams = new URLSearchParams(currentParams.toString());
    nextParams.set('mPage', String(currentMobileStep + 1));
    replaceSearchParams(pathname, router, nextParams);
  };

  return (
    <section className={s.page}>
      <div className={s.content}>
        <div className={s.hero}>
          <div className={s.heroCopy}>
            <h1>{title}</h1>
            <p>{subtitle}</p>
          </div>
        </div>

        {isBannerVisible ? (
          <div className={s.hubWarningBannerWrap}>
            <McpInstallWarningBanner variant='hub' onDismiss={() => setIsBannerVisible(false)} />
          </div>
        ) : null}

        <div className={s.panel}>
          <div className={s.toolbar}>
            <Input
              className={s.search}
              defaultValue={query}
              placeholder={t('skills_search_placeholder')}
              prefix={<SearchIcon />}
              size='lg'
              onValueChange={(value) =>
                updateQueryParam({
                  pathname,
                  router,
                  searchParams: new URLSearchParams(searchParams?.toString()),
                  key: 'q',
                  value: value || null,
                  resetPage: true,
                  resetMobilePage: true,
                })
              }
            />
            {shouldUseMobileCards ? null : (
              <ViewToggle
                view={currentView}
                onChange={(view) =>
                  updateQueryParam({
                    pathname,
                    router,
                    searchParams: new URLSearchParams(searchParams?.toString()),
                    key: 'view',
                    value: view === DEFAULT_VIEW ? null : view,
                    resetPage: true,
                    resetMobilePage: true,
                  })
                }
              />
            )}
          </div>

          {skills.length > 0 ? (
            shouldUseMobileCards || currentView === 'grid' ? (
              <>
                <div className={s.grid}>
                  {displayedSkills.map((skill) => (
                    <GridCard key={skill.id} skill={skill} onOpen={openSkillDetail} />
                  ))}
                </div>
                {canLoadMoreMobile ? (
                  <div className={s.mobileLoadMore}>
                    <button
                      className={s.mobileLoadMoreButton}
                      type='button'
                      onClick={handleMobileLoadMore}
                    >
                      {t('view_more')}
                    </button>
                  </div>
                ) : null}
              </>
            ) : (
              <div className={s.listView}>
                <div className={s.listHeader}>
                  <span>{t('skills_column_name')}</span>
                  <span>{t('skills_column_developer')}</span>
                  <span>{t('skills_column_category')}</span>
                  <span>{t('skills_column_actions')}</span>
                </div>
                <div className={s.listBody}>
                  {skills.map((skill) => (
                    <ListRow key={skill.id} skill={skill} onOpen={openSkillDetail} />
                  ))}
                </div>
              </div>
            )
          ) : (
            <div className={s.emptyState}>{t('skills_no_results')}</div>
          )}

          {shouldUseMobileCards ? null : (
            <Pagination
              currentPage={currentPage}
              pathname={pathname}
              router={router}
              searchParams={new URLSearchParams(searchParams?.toString())}
              totalItems={total}
              totalPages={totalPages}
              view={currentView}
            />
          )}
        </div>
      </div>
      {selectedSkillId ? (
        <SkillDetailOverlay
          detail={selectedSkillDetail}
          fallbackSkill={fallbackSkill}
          isBannerVisible={isBannerVisible}
          isLoggedIn={isLoggedIn}
          onBannerDismiss={() => setIsBannerVisible(false)}
          onClose={closeSkillDetail}
        />
      ) : null}
    </section>
  );
}
